import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { describeError } from '../describeError';
import { detectedTypeWord, fileTypeLabel, formatDateRange, formatFileSize, sourceSystemLabel } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { payrollHistoryImportGateway as gateway } from '../index';
import { providerOf } from '../providerLookup';
import { batchStepPath, usesGuidedMapping } from '../routes';
import type { BatchDetail, Capabilities, Structure, TabularSheet } from '../types';
import { ErrorSummary } from './basics';

function typeBreakdown(sheet: TabularSheet): string {
  const counts = new Map<string, number>();
  for (const c of sheet.columns) counts.set(c.detectedType, (counts.get(c.detectedType) ?? 0) + 1);
  return [...counts.entries()].map(([type, n]) => `${n} ${detectedTypeWord(type)}`).join(', ') || '—';
}

interface Props {
  batch: BatchDetail;
  capabilities: Capabilities;
}

/** Step 3: what EPN identified in the uploaded file (file type, sheet, columns and rows, or runs and employees). */
export function FoundStep({ batch, capabilities }: Props) {
  const guided = usesGuidedMapping(batch);
  const provider = providerOf(batch, capabilities);
  const canRead = ['ColumnMappingRequired', 'ValidationFailed', 'ReadyForReview'].includes(batch.status);

  const load = useCallback(
    (signal: AbortSignal): Promise<Structure | null> => (guided && canRead ? gateway.getStructure(batch.id, undefined, signal) : Promise.resolve(null)),
    [batch.id, guided, canRead],
  );
  const { loading, data: structure, error, reload } = useAsyncData(load);

  const next = guided
    ? { to: batchStepPath(batch.id, 'map'), label: 'Next: Match your columns' }
    : { to: batchStepPath(batch.id, 'employees'), label: 'Next: Match your employees' };
  const canContinue = canRead && batch.scanStatus !== 'Failed';

  if (loading) return <p className="muted" role="status">Looking at your file…</p>;
  if (error !== undefined) {
    return (
      <div>
        <ErrorSummary message={describeError(error, 'Could not read the structure of your file.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
      </div>
    );
  }

  const sheet = structure?.sheets.find((s) => s.name === structure.selectedSheet) ?? structure?.sheets[0];

  return (
    <div>
      <p>Here is what EPN identified in your file. Check that it looks right before you continue.</p>

      <section aria-labelledby="phi-found-heading">
        <h3 id="phi-found-heading">What we found</h3>
        <dl className="phi-facts" aria-label="What we found in your file">
          <dt>File name</dt><dd>{batch.fileName ?? '—'}</dd>
          <dt>Size</dt><dd>{formatFileSize(batch.fileSizeBytes)}</dd>
          <dt>Source</dt><dd>{provider ? provider.displayName : sourceSystemLabel(batch.sourceSystem)}</dd>
          {provider && <><dt>How EPN reads it</dt><dd>{provider.supportLabel}</dd></>}
          <dt>File type</dt><dd>{fileTypeLabel(batch)}</dd>
          {structure && sheet ? (
            <>
              <dt>Sheet</dt>
              <dd>{sheet.name}{structure.sheets.length > 1 ? ` (1 of ${structure.sheets.length} sheets)` : ''}</dd>
              <dt>Columns</dt><dd>{sheet.columns.length}</dd>
              <dt>Rows of data</dt><dd>{sheet.rowCount}</dd>
              <dt>What the columns hold</dt><dd>{typeBreakdown(sheet)}</dd>
            </>
          ) : (
            <>
              <dt>Payroll runs</dt><dd>{batch.payrollRunCount}</dd>
              <dt>Employees</dt><dd>{batch.employeeCount}</dd>
              <dt>Pay dates</dt><dd>{formatDateRange(batch.payDateStart, batch.payDateEnd)}</dd>
            </>
          )}
        </dl>
      </section>

      {guided ? (
        <>
          <p className="muted">
            EPN reads only each column&apos;s heading and the kind of data it holds. None of the values in your file are shown on screen.
          </p>
          {sheet && (
            <details className="phi-notice">
              <summary>Show the {sheet.columns.length} columns we found</summary>
              <div className="table-scroll">
                <table>
                  <caption>Columns found in your file</caption>
                  <thead>
                    <tr><th scope="col">Column heading</th><th scope="col">Holds</th></tr>
                  </thead>
                  <tbody>
                    {sheet.columns.map((c) => (
                      <tr key={c.index}><th scope="row">{c.header || '(no heading)'}</th><td>{detectedTypeWord(c.detectedType)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </>
      ) : (
        <div className="phi-notice">
          <p>Your file already uses the EPN layout, so you do not need to match columns.</p>
          <p className="muted">The next step matches the employees in your file to your EPN employees.</p>
        </div>
      )}

      <div className="phi-footer">
        <Link to={batchStepPath(batch.id, 'upload')} className="phi-btn-secondary">Back</Link>
        {canContinue ? (
          <Link to={next.to} className="phi-btn-primary">{next.label}</Link>
        ) : (
          <button type="button" disabled>{next.label}</button>
        )}
      </div>
    </div>
  );
}
